import { IMGIX, IMGIX_SETTINGS_MINIFIED } from '../../constants'
const { NEXT_PUBLIC_VEE_ENDPOINT, BASE_PATH } = process.env
import * as Sentry from '@sentry/nextjs'
import moment from 'moment'
import { getAuthorUrl } from '../../utilities/getAuthorUrl'
import { Client, gql } from 'urql'
import { Author } from '../../models/types/Author.type'
import { Article } from '../../models/types/Article.type'
import { Space } from '../../models/types/Space.type'

const TOP_LEVEL_NICHE_QUERY = gql`
  query TopLevelNicheTypes($locale: String!) {
    nicheTypes(
      where: {
        parent_niche_type_null: true
        parent_event_type_null: true
        is_single_instance_page: false
        published: true
        locale: $locale
      }
    ) {
      id
    }
  }
`

type EventType = {
  child_niche_types: NicheType[]
}

type NicheType = {
  id: string
  locale: string
  niche_type: {
    child_niche_types: NicheType[]
    siblings: NicheType[]
    parent_event_type: EventType | null
    parent_niche_type: NicheType | null
  }
}

export const insertDynamicText = (content = '', location = '', nicheOrEventName = '') => {
  if (location.length === 0) {
    return content
      ?.replace(/{{niche}}/g, nicheOrEventName)
      .replace(/{{location}}'s/g, '')
      .replace(/in {{location}}/g, '')
      .replace(/{{location}}/g, '')
  }
  const formattedText = content?.replace(/{{location}}/g, location).replace(/{{niche}}/g, nicheOrEventName)

  return formattedText
}

type CreateSchemaProps = {
  spaces: Space[]
  breadcrumbs: any
  title: string
  faqs: any
  article: Article
  query: any
  location: string
  nicheOrEventName: string
  ilpSlug: string
  locale: string
  author: Author
}

export const createSchema = ({
  spaces,
  breadcrumbs,
  title,
  faqs,
  article,
  query,
  location = '',
  nicheOrEventName = '',
  ilpSlug = '',
  locale,
  author
}: CreateSchemaProps) => {
  let breadcrumbsSchema = null
  let faqSchema = null
  let venuesSchema = null
  let articleSchema = null
  let authorPageSchema = null

  if (author) {
    authorPageSchema = {
      '@context': 'https://schema.org',
      '@type': 'Person',
      image: author.url,
      jobTitle: author.jobTitle,
      name: author.firstName + ' ' + author.lastName
    }
  }

  if (article) {
    const img = article.venues[0]?.cover[0]?.hash + article.venues[0]?.cover[0]?.ext
    articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      author: article.author[0] && {
        '@type': 'Person',
        name: article.author[0].firstName + ' ' + article.author[0].lastName,
        url: getAuthorUrl(
          article.author[0].id,
          article.author[0].firstName,
          article.author[0].lastName,
          article.author[0].locale,
          BASE_PATH
        )
      },
      name: insertDynamicText(article.title, location, nicheOrEventName),
      mainEntityOfPage: `${BASE_PATH}/${query.slug.join('/')}`,
      url: `${BASE_PATH}/${query.slug.join('/')}`,
      headline: insertDynamicText(article.title, location, nicheOrEventName),
      description: article.introduction,
      datePublished: article.created_at,
      publisher: { '@type': 'Organisation', name: 'HeadBox' },
      dateModified: article.updated_at,
      image: IMGIX + img + IMGIX_SETTINGS_MINIFIED,
      articleBody: `${article.venues.map(
        (e, i) => ` ${i + 1}. ${e.name} in ${e.location}. ${e.introduction.replace(/(\r\n|\n|\r)/gm, '')}` // remove markdown
      )}`
    }
  }

  if (faqs && faqs.faqs?.length > 0) {
    const listOfFaqs = faqs.faqs?.map(
      ({ question, answer }: { question: string; answer: string }, index: number) => {
        return {
          '@type': 'Question',
          position: index + 1,
          name: question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer
          }
        }
      }
    )
    faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: listOfFaqs
    }
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    const listOfBreadcrumbs = [
      ...[...breadcrumbs].reverse().map(({ display, href }, i) => {
        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@id': `${process.env.BASE_PATH}${locale !== 'GB' ? `/${locale.toLowerCase()}` : ''}${href}`,
            name: display
          }
        }
      }),
      {
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        item: {
          name: title,
          '@id': `${process.env.BASE_PATH}/${ilpSlug}`
        }
      }
    ]
    breadcrumbsSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: listOfBreadcrumbs
    }
  }

  if (spaces) {
    // filter spaces so we only build the structured data once, even if space appears multiple time in page, time O(n)
    const seen = new Set()
    const filteredSpaces = spaces.filter((space) => {
      const string = `${space.venue.name}, ${space.name}`
      const duplicate = seen.has(string)
      seen.add(string)
      return !duplicate
    })

    venuesSchema = {
      '@context': 'https://schema.org',
      name: title,
      '@type': 'ItemList',
      itemListElement: filteredSpaces.map((space, i) => {
        const listItem = {
          '@type': 'ListItem',
          position: `${i + 1}`,
          item: {
            '@id': `${space.venue.name}, ${space.name}`,
            '@type': 'LocalBusiness',
            name: `${space.venue.name}, ${space.name}`,
            description: space.description,
            image: IMGIX + Object.values(space.photos)[0]?.[0]?.[0]?.s3_key + IMGIX_SETTINGS_MINIFIED,
            geo: {
              '@type': 'GeoCoordinates',
              latitude: space.venue.lat,
              longitude: space.venue.lon
            },
            aggregateRating: {},
            address: {}
          }
        }

        if (
          space.venue.google_reviews_enabled &&
          space.venue.google_rating &&
          space.venue.google_total_ratings
        ) {
          listItem.item.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: space.venue.google_rating,
            ratingCount: space.venue.google_total_ratings
          }
        }

        if (space.venue.google_address) {
          listItem.item.address = {
            '@type': 'PostalAddress',
            streetAddress: `${space.venue.google_address.streetNumber ?? null} ${
              space.venue.google_address.street ?? null
            }`,
            addressLocality: space.venue.google_address.city ?? null,
            addressRegion: space.venue.google_address.region ?? null,
            postalCode: space.venue.google_address.postCode ?? null,
            addressCountry: space.venue.google_address.country ?? null
          }
        }

        return listItem
      })
    }
  }

  return {
    venues: venuesSchema,
    faqs: faqSchema,
    breadcrumbs: breadcrumbsSchema,
    article: articleSchema,
    author: authorPageSchema
  }
}

export const createLinkPath = ({
  locale,
  seoSlug,
  nicheOrEventSlug
}: {
  locale: string
  seoSlug: string
  nicheOrEventSlug: string
}) =>
  [
    '',
    ...(locale !== 'GB' ? [locale.toLowerCase()] : []),
    seoSlug,
    '{{location}}',
    ...(!!nicheOrEventSlug ? [nicheOrEventSlug] : [])
  ].join('/')

export const getCuratedVenues = async (spaceIds: string) => {
  if (spaceIds) {
    const spaceData = await fetch(`${NEXT_PUBLIC_VEE_ENDPOINT}/spaces_by_id?ids=${spaceIds}`)
    let spaces: Space[] = []
    const spacesData = await spaceData.json()
    if (spacesData.spaces) {
      spaces = spacesData.spaces
    }
    spaces.sort((a, b) => spaceIds.indexOf(a.id) - spaceIds.indexOf(b.id))
    return spaces
  }
  return []
}

export async function getRelatedNicheIds(
  eventType: EventType,
  nicheType: NicheType,
  locale: string,
  client: Client
) {
  if (eventType) {
    return nicheIdsFromEventType(eventType, locale)
  }
  if (nicheType) {
    const children = nicheType?.niche_type?.child_niche_types ?? []
    const siblings = nicheType?.niche_type?.siblings ?? []

    const filterAndMapIds = (nicheList: NicheType[]) => {
      return nicheList
        .filter((niche) => {
          if (niche.locale !== locale) {
            Sentry.captureMessage(`nicheType ${niche.id} does not match parent locale`)
            return false
          }
          return true
        })
        .map(({ id }: { id: string }) => parseInt(id))
    }

    const is_top_level_niche =
      nicheType?.niche_type.parent_event_type === null && nicheType?.niche_type.parent_niche_type === null

    return {
      children: filterAndMapIds(children),
      siblings: filterAndMapIds(siblings),
      include_event_types_as_children: false,
      include_event_types_as_siblings: is_top_level_niche
    }
  }
  if (client) {
    const resp = await client
      .query(TOP_LEVEL_NICHE_QUERY, {
        locale
      })
      .toPromise()
    return {
      siblings: [],
      children: resp.data.nicheTypes.map(({ id }: { id: string }) => id),
      include_event_types_as_children: true,
      include_event_types_as_siblings: false
    }
  }
  return { children: [], siblings: [] }
}

export function nicheIdsFromEventType(eventType: EventType, locale: string) {
  const nicheTypes = eventType.child_niche_types
    .filter((niche) => {
      if (niche.locale !== locale) {
        Sentry.captureMessage(`nicheType ${niche.id} does not match parent locale`)
        return false
      }
      return true
    })
    .map(({ id }) => parseInt(id))

  return {
    children: nicheTypes,
    siblings: [],
    include_event_types_as_siblings: true,
    include_event_types_as_children: false
  }
}

export const getFormattedISODate = (date: Date, locale: string) => {
  const start = moment(date)
  const dateFormat = locale === 'US' ? 'MMMM Do yyyy' : 'Do MMMM yyyy'
  return start.utc().format(dateFormat)
}
